import { Component, OnInit } from '@angular/core';
import { ClaimKey } from '../../../../../models/api/core/claim-key';
import { ClaimRow } from '../../../../../models/api/view/claim-view/claim-row';
import { ApportionmentData } from '../../../../../models/api/view/claim/details/detail/workers-compensation/mmi/apportionment-data';
import { ApportionmentDetails } from '../../../../../models/api/view/claim/details/detail/workers-compensation/mmi/apportionment-details';
import { MMIDetails } from '../../../../../models/api/view/claim/details/detail/workers-compensation/mmi/mmi-details';
import { FormatPipe } from '../../../../../pipes/format.pipe';
import { JoinPipe } from '../../../../../pipes/join.pipe';
import { BaseComponent } from '../../../../base/base.component';
import { AddressLabelComponent } from '../../../../core/labels/address-label.component';
import { ProgressbarComponent } from '../../../../core/labels/progressbar.component';
import { SsnLabelComponent } from '../../../../core/labels/ssn-label.component';
import { CardComponent } from '../../../../core/panels/card.component';
import { DetailItemComponent } from '../../../../core/panels/detail-panel/detail-item.component';
import { DetailPanelComponent } from '../../../../core/panels/detail-panel/detail-panel.component';
import { EmptyStateComponent } from '../../../../core/panels/empty-state.component';
import { LoadingPanelComponent } from '../../../../core/panels/loading-panel.component';
import { ColumnComponent } from '../../../../core/table/column.component';
import { TableComponent } from '../../../../core/table/table.component';
import { HandleLoadingState } from '../../../../../decorators/handle-loading-state.decorator';

@Component({
  selector: 'app-mmi-wc',
  imports: [
    DetailPanelComponent,
    DetailItemComponent,
    CardComponent,
    TableComponent,
    ColumnComponent,
    AddressLabelComponent,
    SsnLabelComponent,
    JoinPipe,
    FormatPipe,
    EmptyStateComponent,
    ProgressbarComponent,
    LoadingPanelComponent,
  ],
  template: `
    <app-loading-panel [state]="loading ? 'Loading' : 'Loaded'" [error]="error">
      <ng-template #loaded>
        <div
          class="tw-flex tw-flex-col xl:tw-flex-row tw-gap-4 md:tw-gap-8 tw-pb-4 tw-pt-6"
        >
          <div class="tw-w-full xl:tw-w-1/3">
            <app-card label="Impairment">
              <app-detail-panel [dataSource]="mmiDetails">
                <app-detail-item
                  label="impairment percentage"
                  name="impairmentPercent"
                  format="%"
                />
                <app-detail-item
                  label="impairment type"
                  name="impairmentType"
                />
                <app-detail-item
                  label="determination method"
                  name="determinationMethod"
                />
                <app-detail-item
                  label="Date of MMI"
                  name="mmiDate"
                  labelCase="none"
                  format="d"
                />
              </app-detail-panel>
            </app-card>
          </div>
          <div class="tw-w-full xl:tw-w-1/3">
            <app-card label="Settlement">
              <app-detail-panel [dataSource]="mmiDetails">
                <app-detail-item
                  label="settlement method"
                  name="settlementMethod"
                />
                <app-detail-item
                  label="settlement date"
                  name="settlementDate"
                  format="d"
                />
                <app-detail-item
                  label="settlement type"
                  name="settlementType"
                />
              </app-detail-panel>
            </app-card>
          </div>
          <div class="tw-w-full xl:tw-w-1/3">
            <app-card label="Apportionment">
              <app-detail-panel [dataSource]="mmiDetails">
                <app-detail-item
                  label="apportionment percentage"
                  name="apportionmentPercentage"
                  format="%"
                />
                <app-detail-item
                  label="date apportionment set"
                  name="apportionmentSetDate"
                  format="d"
                />
              </app-detail-panel>
            </app-card>
          </div>
        </div>
        <div
          class="tw-flex tw-flex-col xl:tw-flex-row tw-gap-4 md:tw-gap-8 tw-pb-4"
        >
          <div class="tw-basis-full">
            <app-card
              label="Apportionment detail"
              ariaLabel="Apportionment detail table"
            >
              <app-table
                [dataSource]="apportionmentData"
                [pageSize]="5"
                recordLabel="Apportionment detail"
                [showExport]="false"
                [expandable]="true"
                [stripedRows]="false"
                [hideExpandAll]="true"
                dataKey="apportionmentMasterUID"
                [error]="error"
                (rowExpand)="onRowExpand($event)"
              >
                <app-column name="partTarget" label="Part/Target" />
                <app-column name="description" label="Description" />
                <app-column name="bodySide" label="Body Side" />
                <app-column
                  name="dateOfApportionment"
                  label="Date of apportionment"
                  format="d"
                />
                <ng-template #expand let-data>
                  @if (rowLoading) {
                    <app-empty-state
                      icon="fa-light fa-file-arrow-down"
                      label="Loading apportionment details"
                    >
                      <div class="tw-py-4">
                        <app-progressbar />
                      </div>
                    </app-empty-state>
                  } @else {
                    <div class="tw-py-4">
                      <app-table
                        [dataSource]="apportionmentDetails"
                        theme="Dark"
                        [pageSize]="5"
                        [showToolbar]="false"
                        [expandable]="true"
                        dataKey="apportDetailUID"
                        [stripedRows]="false"
                        [error]="rowExpandError"
                      >
                        <app-column
                          name="apportPercentage"
                          label="Apportionment %"
                        />
                        <app-column name="payerType" label="Payer type" />
                        <app-column name="lastName" label="Name">
                          <ng-template let-data>
                            {{
                              [data.firstName, data.middleName, data.lastName]
                                | join
                            }}
                          </ng-template>
                        </app-column>

                        <ng-template #expand let-data
                          ><div class="tw-flex tw-gap-8 ">
                            <app-detail-panel
                              [dataSource]="data"
                              class="tw-basis-1/3"
                            >
                              @if (showTaxId || data.payerType !== 'Claimant') {
                                <app-detail-item
                                  label="Tax ID"
                                  name="taxId"
                                  labelCase="none"
                                >
                                  <ng-template let-data #value>
                                    @if (data.isSsn) {
                                      <app-ssn-label [value]="data.taxId" />
                                    } @else {
                                      <span>{{ data.taxId }}</span>
                                    }
                                  </ng-template>
                                </app-detail-item>
                              }

                              <app-detail-item
                                label="link to provider"
                                name="linkToProvider"
                              />
                              <app-detail-item label="address" name="address1">
                                <ng-template let-data #value>
                                  <app-address-label
                                    [street1]="
                                      [data.address1, data.address2] | join
                                    "
                                    [city]="data.city"
                                    [state]="data.state"
                                    [postalCode]="data.zipCode"
                                  />
                                </ng-template>
                              </app-detail-item>
                            </app-detail-panel>

                            <app-detail-panel
                              [dataSource]="data"
                              class="tw-basis-1/3"
                            >
                              <app-detail-item label="phone 1" name="phone">
                                <ng-template let-data #value>
                                  @if (data.phone) {
                                    {{
                                      [data.phone, data.ext]
                                        | join
                                        | format: 'p'
                                    }}
                                  }
                                </ng-template>
                              </app-detail-item>
                              <app-detail-item
                                label="contact name"
                                name="contactName"
                              />
                              <app-detail-item
                                label="contact phone"
                                name="contactNumber"
                                format="p"
                              />
                            </app-detail-panel></div
                        ></ng-template>
                      </app-table>
                    </div>
                  }
                </ng-template>
              </app-table>
            </app-card>
          </div>
        </div>
      </ng-template>
    </app-loading-panel>
  `,
  styles: ``,
})
export class MmiWcComponent extends BaseComponent implements OnInit {
  claimRow: ClaimRow;
  mmiDetails: MMIDetails;
  apportionmentData: ApportionmentData[] = [];
  apportionmentDetails: ApportionmentDetails[] = [];
  showTaxId = false;
  rowLoading = false;

  loading = false;
  error: any = null;
  rowExpandError: any = null;

  @HandleLoadingState()
  async ngOnInit() {
    this.claimRow = this.view.currentClaim();
    const claimKey = this.getClaimKey();
    this.showTaxId =
      !this.session.user?.fieldSecurity?.restrictedFields?.includes('TaxId');
    const mmiResponse = await this.api.mmi.get(
      claimKey,
      this.claimRow.contractNumber,
    );
    this.mmiDetails = mmiResponse.mmiDetails;
    this.apportionmentData = mmiResponse.apportionmentData;
  }

  @HandleLoadingState({
    loadingProperty: 'rowLoading',
    errorProperty: 'rowExpandError',
  })
  async onRowExpand(event: { data: ApportionmentData }) {
    const claimKey = this.getClaimKey();
    this.apportionmentDetails = await this.api.mmi.getMMIDetails(
      claimKey,
      this.claimRow.contractNumber,
      event.data.apportionmentMasterUID,
    );
  }

  getClaimKey(): ClaimKey {
    return {
      recordSource: this.claimRow.recordSource,
      recordId: this.claimRow.recordId,
    };
  }
}
